
// tslint:disable-next-line:no-implicit-dependencies
import * as Aria from 'aria';
import { IClonedEvent } from '@ms/telemetry-manager/lib/IClonedEvent';
import { ClonedEventType } from '@ms/telemetry-manager/lib/EventBase';
import { capitalize } from '@ms/utilities-strings/lib/index';
import { EventFieldType } from '@ms/telemetry-manager/lib/IEvent';
import * as ErrorHelper from '@ms/telemetry-manager/lib/ErrorHelper';

/**
 * Receives an event produced by the telemetry system and extracts and formats the data suitable
 * for logging to Aria.
 */
export function getAriaEventInfo(this: void, event: IClonedEvent): {
  name: string;
  values: { [key: string]: string | number | boolean; };
} {
  const values: { [key: string]: string | number | boolean; } = {
    'CorrelationVector': event.vector.toString(),
    'ValidationErrors': event.validationErrors,
    'WebLog_FullName': event.eventName,
    'WebLog_EventType': ClonedEventType[event.eventType]
  };

  if (event.eventType === ClonedEventType.End) {
    values.Duration = event.endTime - event.startTime;
  }

  const names = event.eventName.split(',');
  for (const name of names) {
    if (name) {
      values[`WebLog_Type_${name}`] = 1;
    }
  }

  const {
    data,
    context
  } = event;

  if (context) {
    for (const key in context) {
      if (Object.prototype.hasOwnProperty.call(context, key)) {
        const value = context[key];

        if (value === undefined || value === null) {
          continue;
        }

        const loggingName = capitalize(key);

        values[loggingName] = value;
      }
    }
  }

  if (data) {
    for (const field in data) {
      if (Object.prototype.hasOwnProperty.call(data, field)) {
        const value = data[field];
        if (value === undefined || value === null) {
          continue;
        }

        const propertyMetadata = event.metadata[field];
        if (propertyMetadata) {
          const loggingName = propertyMetadata.isPrefixingDisabled ?
            capitalize(field) :
            `${capitalize(propertyMetadata.definedInName)}_${field}`;
          const type = propertyMetadata.type;

          if (type === EventFieldType.Object) {
            for (const subField in value) {
              if (Object.prototype.hasOwnProperty.call(value, subField)) {
                const subValue = value[subField];

                if (subValue !== undefined && subValue !== null && subValue !== '') {
                  // Do not write values which would serialize as empty since they do not provide meaningful
                  // information in instrumentation back-ends.
                  values[`${loggingName}_${subField.replace('.', '_')}`] = subValue;
                }
              }
            }
          } else {
            values[loggingName] = type === EventFieldType.Enum ? propertyMetadata.typeRef[value] : value;
          }
        }
      }
    }
  }

  return {
    name: event.isEventTypePrefixingDisabled ? names[names.length - 2] : `ev_${names[names.length - 2]}`,
    values: values
  };
}

/**
 * Given an Aria implementation, builds an event suitable for logging.
 */
export function buildAriaEvent(this: void, ariaTelemetry: typeof Aria, event: IClonedEvent): Aria.EventProperties {
  const {
    name,
    values
  } = getAriaEventInfo(event);

  const eventProperties: Aria.EventProperties = new ariaTelemetry.EventProperties();

  eventProperties.name = name;

  setProperties(ariaTelemetry, eventProperties, values);

  return eventProperties;
}

function setProperties(
  this: void,
  ariaTelemetry: typeof Aria,
  properties: Aria.EventProperties,
  values: { [key: string]: string | number | boolean; }
): void {
  // We are getting a lot of errorCode 3 aria errors complaining about invalid property keys
  // In order to fix the problem we need to know what the problematic keys are
  let lastKey: string | undefined;
  try {
    for (const key in values) {
      if (Object.prototype.hasOwnProperty.call(values, key)) {
        lastKey = key;
        properties.setProperty(key, values[key]);
      }
    }
  } catch (exception) {
    let errorCode;
    let error;
    if (exception instanceof ariaTelemetry.Exception) {
      errorCode = exception.ErrorCode();
      error = new Error(`Aria error: ${exception.toString()}`);
    }

    if (error) {
      // If it is an aria error that is thrown then log it with the error code and the key we tried to set
      exception.handled = true;
      ErrorHelper.logError(error, {
        errorCode: errorCode,
        propertyKey: lastKey
      });
    }

    // Regardless of what kind of error it was, rethrow the error so we don't try to log the event
    throw exception;
  }
}
